import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Mountain, ArrowLeft, Mail } from 'lucide-react';
import { VerifyIdentityPage } from './VerifyIdentityPage';

interface ChangeEmailPageProps {
  onBack: () => void;
}

export const ChangeEmailPage: React.FC<ChangeEmailPageProps> = ({ onBack }) => {
  const { user, changeEmail, isRecentlyAuthenticated } = useAuth();
  const [newEmail, setNewEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [verified, setVerified] = useState(isRecentlyAuthenticated);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await changeEmail(newEmail);
      setSuccess(true);
    } catch (err: any) {
      if (err.code === 'auth/requires-recent-login') {
        setVerified(false);
      } else {
        setError(err.message || 'Failed to change email');
      }
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center px-4 py-8">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-lg shadow-sm p-8 text-center">
            <div className="flex justify-center mb-4">
              <div className="bg-blue-100 p-3 rounded-full">
                <Mail className="h-12 w-12 text-blue-600" />
              </div>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Check your new email</h2>
            <p className="text-gray-600 mb-1">
              We sent a verification link to <strong>{newEmail}</strong>.
            </p>
            <p className="text-gray-600 text-sm">
              Click the link in that email to confirm the change. Your email won't be updated until you verify it.
            </p>
            <p className="mt-3 text-gray-500 text-xs">
              If the email is already associated with another account, you will not receive an email, and the change will not go through.
            </p>
            <button
              onClick={onBack}
              className="mt-6 text-sm text-blue-600 hover:text-blue-800 font-medium"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!verified) {
    return <VerifyIdentityPage onVerified={() => setVerified(true)} onBack={onBack} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center px-4 py-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="flex justify-center">
            <div className="bg-gradient-to-br from-green-500 to-blue-600 p-3 rounded-xl">
              <Mountain className="h-10 w-10 text-white" />
            </div>
          </div>
          <h2 className="mt-6 text-3xl font-bold text-gray-900">
            Change Email
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Current email: {user?.email}
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6">
          <button
            onClick={onBack}
            className="flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Dashboard
          </button>

          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
                {error}
              </div>
            )}

            <div>
              <label htmlFor="newEmail" className="block text-sm font-medium text-gray-700 mb-1">
                New Email Address
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="newEmail"
                  name="newEmail"
                  type="email"
                  autoComplete="email"
                  required
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  className="appearance-none block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Enter new email"
                />
              </div>
            </div>

            <div className="pt-2">
              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gradient-to-r from-green-500 to-blue-600 hover:from-green-600 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Updating...' : 'Update Email'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
